import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { kBrowserSettingsCore } from "../../../Admin/kReusableCoreTS/kBrowserSettingsCore";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppIndexReader } from "./AppIndex";

// AppImportStatus - what happened to the backup a subscriber uploaded, said to them on the page.
//
// ==================================================================================================
// !! ⛔ BEFORE THIS FILE ONLY THE LOG HEARD A REFUSED IMPORT (owner-picked 2026-10-01)
// ==================================================================================================
//
// The browser puts the database in the subscriber's own Dropbox and says "done". The SERVICE imports
// it on its next cycle - and when it refused the file (not a WhatsApp database, damaged inside, too
// large, Dropbox would not move it) the subscriber's history simply never appeared, with nothing on
// any screen saying why.
//
// ⭐ THIS IS THE CONTRACT FOR BOTH HALVES, AND IT HAS NO HABITAT (no DOM, no transport, no Node): the
// Bridge imports it for the service's own answers (`ImportStatusRules`), the page for reading them, and
// `ImportStatusContract.test.ts` holds the two together. Every code the pipeline can produce is in
// `Codes` with its state and its sentence - one table, so a code the service can write and the page
// cannot say is a failing test rather than an empty line.
//
// ⛔ THE UPLOAD IS NAMED BY DROPBOX'S OWN `rev` - the revision id Dropbox gives each write of a file.
// The page reads it off the answer to its own upload, the service off its listing of the folder, so a
// verdict can never be shown for a different upload: a new upload of the same path is a new `rev`.
//
// ⛔ COUNTS AND SENTENCES ONLY. Nothing here carries a message, a contact's name or a path with a
// number in it - the answer is read by a page and written to a log.
//
// ⛔ NO FORECASTS. A pending state says what is happening ("…"), never how long it will take.

/** One answer from `/import`, read. */
export class ImportAnswer {
    /** False when the answer had no boolean `ok` - an older service with no `/import` route. */
    public Known: boolean = false;
    /** The connection was lost or the answer was not readable - keep waiting, say nothing new. */
    public Lost: boolean = false;
    /** `ok: true` - the upload has a FINAL verdict. */
    public Final: boolean = false;
    public State: string = "";
    public Code: string = "";
    /** The service's own sentence - shown only for a code this page does not know. */
    public Why: string = "";
    public Messages: number = -1;
    public Added: number = -1;
    /** Ask again after this long; 0 = do not ask again. */
    public RetryMs: number = 0;
}

export class AppImportStatus {
    public static readonly Route: string = "/import";

    // ── States ────────────────────────────────────────────────────────────────────────────────────
    /** The service has not looked at this upload yet. */
    public static readonly Waiting: string = "waiting";
    /** The service is working on it right now. */
    public static readonly Importing: string = "importing";
    /** It was looked at and something passing went wrong; the upload is still there and is tried again. */
    public static readonly Retrying: string = "retrying";
    /** The history is kept; the pages are being rebuilt and sent to the subscriber's Dropbox. */
    public static readonly Stored: string = "stored";
    /** FINAL: imported, and the pages hold it. */
    public static readonly Imported: string = "imported";
    /** FINAL: the file cannot be imported. It is left exactly where the subscriber put it. */
    public static readonly Refused: string = "refused";
    /** FINAL: no such upload is waiting any more. */
    public static readonly Gone: string = "gone";

    // ── Codes ─────────────────────────────────────────────────────────────────────────────────────
    public static readonly Queued: string = "queued";
    public static readonly NotRunning: string = "notrunning";
    public static readonly DropboxDown: string = "dropbox";
    public static readonly Postponed: string = "postponed";
    public static readonly NoStorage: string = "nostorage";
    public static readonly Working: string = "importing";
    public static readonly Download: string = "download";
    public static readonly Compare: string = "compare";
    public static readonly Move: string = "move";
    public static readonly Trouble: string = "trouble";
    public static readonly Building: string = "building";
    public static readonly Added: string = "added";
    public static readonly NothingNew: string = "nothingnew";
    public static readonly Beside: string = "beside";
    public static readonly KeptBoth: string = "keptboth";
    public static readonly Empty: string = "empty";
    public static readonly NotWhatsApp: string = "notwhatsapp";
    public static readonly Layout: string = "layout";
    public static readonly Damaged: string = "damaged";
    public static readonly Unreadable: string = "unreadable";
    public static readonly TooBig: string = "toobig";
    public static readonly NotFound: string = "notfound";

    /**
     * ⭐ EVERY CODE AND THE STATE IT BELONGS TO - the one table. A code is never sent under another
     * state, and a code not listed here is one this page shows the service's own sentence for.
     */
    public static readonly Codes: Record<string, string> = {
        queued: "waiting", notrunning: "waiting", dropbox: "waiting", postponed: "waiting", nostorage: "waiting",
        importing: "importing",
        download: "retrying", compare: "retrying", move: "retrying", trouble: "retrying",
        building: "stored",
        added: "imported", nothingnew: "imported", beside: "imported", keptboth: "imported", empty: "imported",
        notwhatsapp: "refused", layout: "refused", damaged: "refused", unreadable: "refused", toobig: "refused",
        notfound: "gone",
    };

    /** What the upload is called in the subscriber's Dropbox - the name the refusals point at. */
    public static readonly FileName: string = "wacommander-msgstore.db";
    /** The service's ceiling for one file, as the refusal says it (`ImportPullRules.MaxBytes`, pinned by a test). */
    public static readonly MaxGb: number = 2;

    /** Longest service sentence the page will show (an unknown code's `why`). */
    public static readonly MaxWhy: number = 400;
    public static readonly MaxRetrySec: number = 600;

    /** A Dropbox `rev`: hex, 9 or more characters. ⛔ An allow-list - it goes into a request and a record. */
    private static readonly RevShape: RegExp = /^[0-9a-f]{9,64}$/;

    /** DOCUMENTED EXCEPTION: one regex test on a value in hand.
     *  ERROR-SUPPRESSED-JUSTIFIED: pure helper, cannot throw. */
    public static IdOk = (Rev: string): boolean => {
        return typeof Rev === "string" && AppImportStatus.RevShape.test(Rev);
    };

    /** DOCUMENTED EXCEPTION: one table lookup. ERROR-SUPPRESSED-JUSTIFIED: pure helper, cannot throw. */
    public static StateOf = (Code: string): string => {
        const Found: string | undefined = AppImportStatus.Codes[nz(Code)];
        if (typeof Found !== "string") { return ""; }
        return Found;
    };

    /** DOCUMENTED EXCEPTION: three comparisons. ERROR-SUPPRESSED-JUSTIFIED: pure helper, cannot throw. */
    public static IsFinal = (State: string): boolean => {
        return State === AppImportStatus.Imported || State === AppImportStatus.Refused
            || State === AppImportStatus.Gone;
    };

    /**
     * Does this final state leave the subscriber needing to bring another file? Refused and gone do;
     * imported does not. DOCUMENTED EXCEPTION: two comparisons. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static AsksAnother = (State: string): boolean => {
        return State === AppImportStatus.Refused || State === AppImportStatus.Gone;
    };

    /**
     * The `rev` in Dropbox's answer to `upload_session/finish`, or "" when it carries none this page
     * can use. ⚠ "" is not a failure of the upload - the file is there - it only means the page cannot
     * ask about it, and it then says what it said before this feature existed.
     */
    public static RevIn = (FinishText: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Raw: Record<string, unknown> = JSON.parse(nz(FinishText)) as Record<string, unknown>;
            if (Raw === null || typeof Raw !== "object") { return ""; }
            if (typeof Raw.rev !== "string") { return ""; }
            if (AppImportStatus.IdOk(Raw.rev as string) === false) { return ""; }
            return Raw.rev as string;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an answer that is not JSON is outside input; "" is the
            // defined outcome (the page falls back to its earlier words).
            return "";
        }
    };

    /**
     * The request. ⛔ ONE PROOF: the sign-up secret when the page holds one, else the archive key
     * (`AppSignUpApi.WithProof`'s rule). "" when there is nothing provable to send - never sent.
     */
    public static Body = (Phone: string, Secret: string, Key: string, Rev: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || AppImportStatus.IdOk(Rev) === false) { return ""; }
            const Out: Record<string, unknown> = { number: nz(Phone).trim() };
            if (nz(Secret).trim().length > 0) {
                Out.secret = nz(Secret).trim();
            } else if (nz(Key).trim().length > 0) {
                Out.key = nz(Key).trim();
            } else {
                return "";
            }
            Out.upload = Rev;
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportStatus.Body");
            return "";
        }
    };

    /**
     * Read an answer.
     *
     * ⛔ STATUS 0 AND A BODY THAT IS NOT JSON ARE "KEEP WAITING" (`Lost`), never a verdict - every
     * deploy produces both for a few seconds, and reading them as a refusal would tell somebody whose
     * import is going fine that it failed. ⚠ No boolean `ok` = the status document of a service with
     * no `/import` route (`Known` false): the page then says what it said before.
     */
    public static Parse = (Status: number, Text: string): ImportAnswer => {
        const Out: ImportAnswer = new ImportAnswer();
        if (getCoreUtils().KErrorMode === true) { Out.Lost = true; return Out; }
        try {
            // ⛔ A 5xx IS "KEEP WAITING" TOO: the service's own sentence for it says the page asks again by
            // itself, and reading it as an answer ended the watch on that very sentence.
            if (Status === 0 || Status >= 500) { Out.Lost = true; return Out; }
            const Read: Record<string, unknown> | null = AppImportStatus.ObjectIn(Text);
            if (Read === null) { Out.Lost = true; return Out; }
            const Raw: Record<string, unknown> = Read as Record<string, unknown>;
            if (typeof Raw.ok !== "boolean") { return Out; }
            Out.Known = true;
            Out.Final = Raw.ok === true;
            if (typeof Raw.state === "string") { Out.State = Raw.state as string; }
            if (typeof Raw.code === "string") { Out.Code = Raw.code as string; }
            if (typeof Raw.why === "string") { Out.Why = (Raw.why as string).substring(0, AppImportStatus.MaxWhy); }
            Out.Messages = AppImportStatus.CountIn(Raw.messages);
            Out.Added = AppImportStatus.CountIn(Raw.added);
            Out.RetryMs = AppImportStatus.RetryIn(Raw.retry);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportStatus.Parse");
            const Bad: ImportAnswer = new ImportAnswer();
            Bad.Lost = true;
            return Bad;
        }
    };

    /** The JSON object in `Text`, or null. ERROR-SUPPRESSED-JUSTIFIED: not-JSON IS the answer null. */
    private static ObjectIn = (Text: string): Record<string, unknown> | null => {
        return AppIndexReader.ObjectIn(Text);
    };

    /** A whole count >= 0, or -1 (not said). DOCUMENTED EXCEPTION: pure, cannot throw. */
    private static CountIn = (Raw: unknown): number => {
        if (typeof Raw !== "number" || isFinite(Raw) === false || Raw < 0) { return -1; }
        return Math.floor(Raw);
    };

    /** `retry` seconds as ms: a whole number 1..600, else 0. DOCUMENTED EXCEPTION: pure, cannot throw. */
    private static RetryIn = (Raw: unknown): number => {
        if (typeof Raw !== "number" || isFinite(Raw) === false) { return 0; }
        const Sec: number = Math.floor(Raw);
        if (Sec < 1 || Sec > AppImportStatus.MaxRetrySec) { return 0; }
        return Sec * 1000;
    };

    /** "1 message" / "12,345 messages". DOCUMENTED EXCEPTION: pure formatting. */
    private static Count = (N: number, Locale: string): string => {
        let Shown: string = String(N);
        if (nz(Locale).length > 0) { Shown = N.toLocaleString(Locale); } else { Shown = N.toLocaleString(); }
        if (N === 1) { return Shown + " message"; }
        return Shown + " messages";
    };

    /**
     * ⭐ THE SENTENCE FOR A CODE - the same words on the service and the page. "" for a code this
     * build does not know (the caller then shows the service's own sentence).
     *
     * ⚠ `Locale` "" formats counts in the reader's own locale (the page); the service passes "en-US".
     * ⛔ A pending sentence ends in the ellipsis character and says what is happening, never how long.
     */
    public static Sentence = (Code: string, Messages: number, Added: number, Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Fixed: string | undefined = AppImportStatus.Words[nz(Code)];
            if (typeof Fixed === "string") { return Fixed; }
            if (Code === AppImportStatus.Added) { return AppImportStatus.AddedWords(Messages, Added, Locale); }
            if (Code === AppImportStatus.KeptBoth) {
                return AppImportStatus.AddedWords(Messages, Added, Locale)
                    + " Your earlier backup is kept too, because it holds messages this one does not.";
            }
            if (Code === AppImportStatus.Beside) {
                let Tail: string = "";
                if (Added > 0) { Tail = " It added " + AppImportStatus.Count(Added, Locale) + "."; }
                return "Imported: this backup is older than the one WACommander already had, so it is"
                    + " kept beside it." + Tail;
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportStatus.Sentence");
            return "";
        }
    };

    /** "Imported: 12,345 messages, 2,100 of them new." DOCUMENTED EXCEPTION: pure formatting. */
    private static AddedWords = (Messages: number, Added: number, Locale: string): string => {
        if (Messages < 0) { return "Imported."; }
        if (Added < 0 || Added >= Messages) { return "Imported: " + AppImportStatus.Count(Messages, Locale) + "."; }
        let New: string = String(Added);
        if (nz(Locale).length > 0) { New = Added.toLocaleString(Locale); } else { New = Added.toLocaleString(); }
        return "Imported: " + AppImportStatus.Count(Messages, Locale) + ", " + New + " of them new.";
    };

    private static readonly StillThere: string = " The backup is still in your Dropbox, and WACommander is trying again…";
    private static readonly Untouched: string = " Your file is still in your Dropbox, untouched, as "
        + AppImportStatus.FileName + ".";

    /** The sentences that carry no count. ⚠ Static literal data - see `Sentence` for the three that do. */
    private static readonly Words: Record<string, string> = {
        queued: "Your backup is in your Dropbox. Waiting for WACommander to fetch it…",
        notrunning: "Your backup is in your Dropbox. WACommander is starting for your account and fetches it once it is running…",
        dropbox: "Your backup is in your Dropbox. WACommander could not read your Dropbox on its last try and is trying again…",
        postponed: "Your backup is in your Dropbox. WACommander is putting your archive back in place first and imports the backup after that…",
        nostorage: "Your backup is in your Dropbox. WACommander is connecting to your Dropbox and fetches the backup after that…",
        importing: "WACommander is importing your backup…",
        download: "WACommander could not fetch your backup from your Dropbox on its last try." + AppImportStatus.StillThere,
        compare: "WACommander could not compare your backup with the history it already keeps, so it changed nothing."
            + AppImportStatus.StillThere,
        move: "WACommander read your backup, but Dropbox did not let it be put away." + AppImportStatus.StillThere,
        trouble: "WACommander could not import your backup on its last try - this is at our end." + AppImportStatus.StillThere,
        building: "Your backup is imported. WACommander is updating your pages…",
        nothingnew: "Imported - nothing new. WACommander already had every message in this backup.",
        empty: "Your backup was read, but there are no messages in it.",
        notwhatsapp: "WACommander could not read that file as a WhatsApp backup, so nothing was imported."
            + " Import again and choose your phone's WhatsApp backup." + AppImportStatus.Untouched,
        layout: "That is an iPhone WhatsApp database in a layout this version of WACommander cannot read, so"
            + " nothing was imported. Nothing you did is wrong; WACommander tries it again after its next update."
            + AppImportStatus.Untouched,
        damaged: "That database is damaged inside, so nothing was imported. Make a new backup on your phone and"
            + " import that one." + AppImportStatus.Untouched,
        unreadable: "WACommander could not read that backup, so nothing was imported. Make a new backup on your"
            + " phone and import that one." + AppImportStatus.Untouched,
        toobig: "That backup is larger than WACommander accepts in one file (" + String(AppImportStatus.MaxGb)
            + " GB), so nothing was imported." + AppImportStatus.Untouched,
        notfound: "WACommander found no backup waiting in your Dropbox. If your history is not in your archive,"
            + " import the backup again.",
    };

    /** The sentence the page shows before the service has answered at all. */
    public static FirstWords = (): string => {
        // DOCUMENTED EXCEPTION: returns a constant sentence; nothing here can throw.
        return AppImportStatus.Words[AppImportStatus.Queued];
    };
}

/** What the page is showing about one upload. */
export class ImportShown {
    public State: string = "";
    public Code: string = "";
    public Sentence: string = "";
    /** A final verdict, or the service refused the question itself - nothing more will be asked. */
    public Final: boolean = false;
    /** The service has no `/import` route: the page says what it said before this feature. */
    public Off: boolean = false;
    /** When this upload began to be watched (ms) - the elapsed count starts here. */
    public Since: number = 0;

    /** DOCUMENTED EXCEPTION: compares fields in hand. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    public Same = (Other: ImportShown): boolean => {
        return this.State === Other.State && this.Code === Other.Code && this.Sentence === Other.Sentence
            && this.Final === Other.Final && this.Off === Other.Off;
    };
}

/** What the watch needs from its habitat - the browser in the app, a fake in a test. */
export interface IAppImportHost {
    Post: (Url: string, Body: string, Done: (Status: number, Text: string) => void) => void;
    Later: (Ms: number, Work: () => void) => number;
    Cancel: (Handle: number) => void;
    Now: () => number;
    Hidden: () => boolean;
}

export type ImportShownDone = (Shown: ImportShown) => void;

/**
 * The quiet poll: asks `/import` about ONE upload until it has a final verdict.
 *
 * ⛔ ONE REQUEST IN FLIGHT, ONE TIMER. A late answer for an upload that is no longer watched (a new
 * upload, the card left) is dropped by `Seq`. ⛔ A hidden tab asks nothing; coming back asks at once.
 * ⚠ It tells its owner ONLY when what is shown changed - the line is a live region, and writing the
 * same words again would be announced again.
 */
export class AppImportWatch {
    /** A lost connection: wait this long, doubling to `LostMaxMs`. */
    public static readonly LostMs: number = 5000;
    public static readonly LostMaxMs: number = 60000;

    private Host: IAppImportHost;
    private Url: string;
    private Tell: ImportShownDone;
    private Body: string = "";
    /** The upload being (or last) watched. */
    private Rev: string = "";
    private Seq: number = 0;
    private Timer: number = 0;
    private Asking: boolean = false;
    private Running: boolean = false;
    private Backoff: number = 0;
    private Now: ImportShown = new ImportShown();

    public constructor(Host: IAppImportHost, Url: string, Tell: ImportShownDone) {
        this.Host = Host;
        this.Url = nz(Url);
        this.Tell = Tell;
    }

    /** What is shown right now. */
    public Shown = (): ImportShown => {
        // DOCUMENTED EXCEPTION: returns a field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this.Now;
    };

    /** The `rev` of the upload being (or last) watched, or "". */
    public Upload = (): string => {
        // DOCUMENTED EXCEPTION: returns a field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this.Rev;
    };

    /** Is an upload being watched (not final, not stopped)? */
    public Watching = (): boolean => {
        // DOCUMENTED EXCEPTION: returns a field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this.Running;
    };

    /**
     * Watch `Rev`. The first words are shown at once (the owner is told synchronously), then the
     * service is asked. False when there is nothing provable to ask with - nothing is shown.
     */
    public Start = (Phone: string, Secret: string, Key: string, Rev: string, SinceMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            this.Stop();
            const Body: string = AppImportStatus.Body(Phone, Secret, Key, Rev);
            if (Body.length === 0 || this.Url.length === 0) { return false; }
            this.Body = Body;
            this.Rev = Rev;
            this.Running = true;
            this.Backoff = 0;
            const First: ImportShown = new ImportShown();
            First.State = AppImportStatus.Waiting;
            First.Code = AppImportStatus.Queued;
            First.Sentence = AppImportStatus.FirstWords();
            First.Since = SinceMs;
            this.Now = First;
            this.Tell(First);
            this.Ask();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.Start");
            return false;
        }
    };

    /**
     * Carry on asking about the same upload after a `Stop` (the card was left and is back). ⚠ What is
     * shown is NOT reset - the line is a live region and must not be announced again. False when there
     * is nothing to carry on with (never started, or already final).
     */
    public Resume = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Running === true) { return true; }
            if (this.Body.length === 0 || this.Now.Final === true || this.Now.Off === true) { return false; }
            this.Running = true;
            this.Ask();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.Resume");
            return false;
        }
    };

    /** The pending wait's timer, dropped. Ungated: `Stop` must still cancel after a fault.
     *  The inverted condition is deliberate - it is this run's own shape.
     */
    private CancelTimer = (): void => {
        if (this.Timer === 0) { return; }
        this.Host.Cancel(this.Timer);
        this.Timer = 0;
    };

    /** Stop asking. ⚠ What is shown stays as it was; an answer still in flight is dropped. */
    public Stop = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - it only cancels a timer, and a page that
        // has faulted must still stop asking.
        try {
            this.Running = false;
            this.Seq = this.Seq + 1;
            this.Asking = false;
            this.CancelTimer();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.Stop");
        }
    };

    /** The tab was hidden: the pending wait is dropped. An ask in flight is let finish. */
    public OnHidden = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.CancelTimer();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.OnHidden");
        }
    };

    /** The tab is shown again: ask at once. */
    public OnVisible = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Running === false) { return; }
            this.CancelTimer();
            this.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.OnVisible");
        }
    };

    private Ask = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Running === false || this.Asking === true) { return; }
            if (this.Host.Hidden() === true) { return; }
            this.Asking = true;
            new AppImportAsk(this, this.Seq).Send(this.Host, this.Url, this.Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.Ask");
            this.Asking = false;
        }
    };

    private OnTimer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Timer = 0;
            this.Ask();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.OnTimer");
        }
    };

    /** One answer. ⛔ Dropped unless it belongs to the upload being watched now (`Seq`). */
    public OnAnswer = (Seq: number, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Seq !== this.Seq || this.Running === false) { return; }
            this.Asking = false;
            const Got: ImportAnswer = AppImportStatus.Parse(Status, Text);
            if (Got.Lost === true) { this.Wait(this.NextBackoff()); return; }
            this.Backoff = 0;
            const Next: ImportShown = AppImportWatch.ShownFor(Got, this.Now.Since);
            const Changed: boolean = Next.Same(this.Now) === false;
            this.Now = Next;
            if (Next.Final === true || Next.Off === true || Got.RetryMs === 0) {
                this.Running = false;
                Next.Final = Next.Off === false;
                this.Tell(Next);
                return;
            }
            if (Changed === true) { this.Tell(Next); }
            this.Wait(Got.RetryMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.OnAnswer");
        }
    };

    /** DOCUMENTED EXCEPTION: arithmetic on a field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private NextBackoff = (): number => {
        if (this.Backoff === 0) { this.Backoff = AppImportWatch.LostMs; return this.Backoff; }
        this.Backoff = Math.min(AppImportWatch.LostMaxMs, this.Backoff * 2);
        return this.Backoff;
    };

    private Wait = (Ms: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Running === false) { return; }
            if (this.Timer !== 0) { this.Host.Cancel(this.Timer); this.Timer = 0; }
            // ⛔ A HIDDEN TAB WAITS FOR `OnVisible`, it does not keep a timer running.
            if (this.Host.Hidden() === true) { return; }
            this.Timer = this.Host.Later(Ms, this.OnTimer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.Wait");
        }
    };

    /**
     * What an answer shows. ⭐ A KNOWN code is said in this page's own words (its own locale for the
     * counts); an unknown one in the service's sentence; neither = the first words stay.
     */
    public static ShownFor = (Got: ImportAnswer, Since: number): ImportShown => {
        const Out: ImportShown = new ImportShown();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Since = Since;
            if (Got.Known === false) { Out.Off = true; return Out; }
            Out.State = Got.State;
            Out.Code = Got.Code;
            Out.Final = Got.Final;
            let Words: string = "";
            // ⛔ A code is trusted only under ITS OWN state - "imported" words are never shown for a pending answer.
            if (AppImportStatus.StateOf(Got.Code) === Got.State) {
                Words = AppImportStatus.Sentence(Got.Code, Got.Messages, Got.Added);
            }
            if (Words.length === 0) { Words = Got.Why; }
            if (Words.length === 0) { Words = AppImportStatus.FirstWords(); }
            Out.Sentence = Words;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportWatch.ShownFor");
            return Out;
        }
    };
}

/** One ask carried across its answer (no closures - the house rule). */
class AppImportAsk {
    private Owner: AppImportWatch;
    private Seq: number;

    public constructor(Owner: AppImportWatch, Seq: number) {
        this.Owner = Owner;
        this.Seq = Seq;
    }

    public Send = (Host: IAppImportHost, Url: string, Body: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Host.Post(Url, Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportAsk.Send");
            this.Owner.OnAnswer(this.Seq, 0, "");
        }
    };

    private OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.OnAnswer(this.Seq, Status, Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportAsk.OnDone");
        }
    };
}

/** The three storage calls the memory uses - `window.localStorage` in the app, a map in a test. */
export interface IAppImportStorage {
    getItem: (Key: string) => string | null;
    setItem: (Key: string, Value: string) => void;
    removeItem: (Key: string) => void;
}

/** A remembered upload. */
export class ImportRemembered {
    public Rev: string = "";
    public At: number = 0;
}

/**
 * Which upload this browser last made, so a reload can ask about it again.
 *
 * ⚠ `rev`, a hash of the number's digits and a time - never the number, never a key. ⛔ An upload
 * remembered for ANOTHER number is not this subscriber's and is never asked about; one older than
 * `KeepMs` is forgotten. ⚠ Every storage call is wrapped: the accessor throws in private browsing.
 */
export class AppImportMemory {
    public static readonly Key: string = "wac.import.upload";
    public static readonly KeepMs: number = 7 * 24 * 3600 * 1000;

    /** The shared settings class over the injected storage (null = no storage); no page-memory fallback (see `AppBrowserSettings`). */
    private Settings: kBrowserSettingsCore;

    public constructor(Store: IAppImportStorage | null) {
        this.Settings = AppBrowserSettings.Over(new AppImportStoreOf(Store).Get);
    }

    /** FNV-1a over the digits, as hex - the number itself is never stored. DOCUMENTED EXCEPTION: pure. */
    public static Who = (Phone: string): string => {
        const Digits: string = nz(Phone).replace(/\D/g, "");
        let Hash: number = 0x811c9dc5;
        for (let At: number = 0; At < Digits.length; At = At + 1) {
            Hash = Hash ^ Digits.charCodeAt(At);
            Hash = Math.imul(Hash, 0x01000193) >>> 0;
        }
        return Hash.toString(16);
    };

    public Save = (Phone: string, Rev: string, NowMs: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppImportStatus.IdOk(Rev) === false) { return; }
            // Storage that refuses (private browsing, quota) is answered by the shared class as a plain outcome: it costs only
            // the reload convenience; the page in hand still shows the status.
            this.Settings.WriteJson(AppImportMemory.Key, { rev: Rev, who: AppImportMemory.Who(Phone), at: NowMs });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportMemory.Save");
        }
    };

    /** The upload remembered for `Phone`, or null. */
    public Load = (Phone: string, NowMs: number): ImportRemembered | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            // Nothing stored, a refused read and damaged JSON all come back as the shared class's fallback (null) = nothing remembered.
            const Raw: Record<string, unknown> | null = this.Settings.ReadJson<Record<string, unknown> | null>(AppImportMemory.Key, null).Value;
            if (Raw === null || typeof Raw !== "object") { return null; }
            if (typeof Raw.rev !== "string" || typeof Raw.who !== "string" || typeof Raw.at !== "number") { return null; }
            if (AppImportStatus.IdOk(Raw.rev as string) === false) { return null; }
            if (Raw.who !== AppImportMemory.Who(Phone)) { return null; }
            const Age: number = NowMs - (Raw.at as number);
            if (Age < 0 || Age > AppImportMemory.KeepMs) { return null; }
            const Out: ImportRemembered = new ImportRemembered();
            Out.Rev = Raw.rev as string;
            Out.At = Raw.at as number;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportMemory.Load");
            return null;
        }
    };

    public Clear = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // A storage that refuses the removal is a defined outcome of the shared class (see `Save`).
            this.Settings.Remove(AppImportMemory.Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportMemory.Clear");
        }
    };
}

/** The injected storage as a getter, for `AppBrowserSettings.Over` (a class, not a closure). */
export class AppImportStoreOf {
    private Store: IAppImportStorage | null;

    public constructor(Store: IAppImportStorage | null) {
        this.Store = Store;
    }

    /** DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Get = (): IAppImportStorage | null => {
        return this.Store;
    };
}

/** The status block's markup and the words beside it. Strings only - the app's habitat paints them. */
export class AppImportView {
    public static readonly BlockId: string = "wac-impwhy";
    public static readonly LineId: string = "wac-impline";
    public static readonly WaitId: string = "wac-impwait";

    /**
     * The block: the sentence (a live region, written on change only) and the elapsed count beside it
     * (hidden from a screen reader - it changes every second). "" when nothing is shown.
     */
    public static Html = (Shown: ImportShown | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Shown === null || Shown.Off === true || nz(Shown.Sentence).length === 0) { return ""; }
            return "<div class=\"impwhy\" id=\"" + AppImportView.BlockId + "\" data-state=\""
                + kHTMLCore.EscapeHtml(AppImportView.Look(Shown)) + "\">"
                // The spinner is drawn only while `data-state` is "busy" (the stylesheet's rule), so a change
                // of state in place needs no new markup.
                + "<span class=\"spin\" aria-hidden=\"true\"></span><div class=\"impbody\">"
                + "<p class=\"impline\" id=\"" + AppImportView.LineId + "\" role=\"status\">"
                + kHTMLCore.EscapeHtml(Shown.Sentence) + "</p>"
                + "<p class=\"impwait\" id=\"" + AppImportView.WaitId + "\" aria-hidden=\"true\"></p></div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportView.Html");
            return "";
        }
    };

    /** The look word the stylesheet keys on: `busy`, `done`, `refused` or `plain`. */
    public static Look = (Shown: ImportShown): string => {
        // DOCUMENTED EXCEPTION: comparisons on fields in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        if (Shown.State === AppImportStatus.Imported) { return "done"; }
        if (Shown.State === AppImportStatus.Refused) { return "refused"; }
        if (Shown.Final === true) { return "plain"; }
        return "busy";
    };

    /** "12 s" / "3 min 05 s" since the watch began - "" once it is final. */
    public static Waited = (Shown: ImportShown | null, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Shown === null || Shown.Final === true || Shown.Off === true || Shown.Since <= 0) { return ""; }
            return AppImportView.Elapsed(Math.max(0, Math.floor((NowMs - Shown.Since) / 1000)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportView.Waited");
            return "";
        }
    };

    /**
     * Whole seconds as "12 s" / "3 min 05 s" - the ONE wording of a ticking wait, for the import watch (`Waited`)
     * and the sign-up's wait for a code (`AppSignUp.WaitedText`). `Sec` arrives floored and not negative.
     */
    public static Elapsed = (Sec: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Sec < 60) { return String(Sec) + " s"; }
            const Rest: number = Sec % 60;
            let Pad: string = String(Rest);
            if (Rest < 10) { Pad = "0" + Pad; }
            return String(Math.floor(Sec / 60)) + " min " + Pad + " s";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppImportView.Elapsed");
            return "";
        }
    };

    /** The heading of the block when it stands above the import step after a reload. */
    public static readonly EarlierHead: string = "Your last upload";
}
